import { Statement, Expression, ForLoopNode, WhileLoopNode, DoWhileLoopNode } from '../types'

/**
 * 実行コンテキスト - 現在の実行位置と状態を管理
 */
export interface ExecutionContext {
  // 現在実行中のステートメントのインデックス
  statementIndex: number
  // 親コンテキスト（ネストした構造用）
  parent?: ExecutionContext
  // このコンテキストのステートメントリスト
  statements: Statement[]
  // ループ用の追加情報
  loopInfo?: LoopExecutionInfo
}

export interface LoopExecutionInfo {
  type: 'for' | 'while' | 'do-while'
  // forループ用
  initExecuted?: boolean
  conditionChecked?: boolean
  updateExecuted?: boolean
  // 共通
  iterationCount: number
  loopNode: ForLoopNode | WhileLoopNode | DoWhileLoopNode
}

export class ExecutionContextManager {
  private contextStack: ExecutionContext[] = []
  
  constructor(statements: Statement[]) {
    // ルートコンテキストを作成
    this.contextStack.push({
      statementIndex: 0,
      statements: statements,
    })
  }
  
  get currentContext(): ExecutionContext | undefined {
    return this.contextStack[this.contextStack.length - 1]
  }
  
  get currentStatement(): Statement | undefined {
    const context = this.currentContext
    if (!context) return undefined
    return context.statements[context.statementIndex]
  }
  
  /**
   * 次のステートメントに進む
   * @returns 次のステートメントがあるかどうか
   */
  advance(): boolean {
    const context = this.currentContext
    if (!context) return false
    
    context.statementIndex++
    
    // 現在のコンテキストの終端に達した場合
    if (context.statementIndex >= context.statements.length) {
      // ループの場合は条件をチェック
      if (context.loopInfo) {
        return this.handleLoopEnd(context)
      }
      
      // ループでない場合は親コンテキストに戻る
      this.popContext()
      // 親コンテキストがある場合は、親も次に進める
      if (this.currentContext) {
        return this.advance()
      }
      return false
    }
    
    return true
  }
  
  /**
   * 新しいコンテキストをプッシュ（ブロックやループの開始）
   */
  pushContext(statements: Statement[], loopInfo?: LoopExecutionInfo): void {
    this.contextStack.push({
      statementIndex: 0,
      parent: this.currentContext,
      statements,
      loopInfo,
    })
  }
  
  /**
   * 現在のコンテキストをポップ（ブロックやループの終了）
   */
  popContext(): void {
    this.contextStack.pop()
  }
  
  /**
   * ループの終端処理
   */
  private handleLoopEnd(context: ExecutionContext): boolean {
    if (!context.loopInfo) return false
    
    const loopInfo = context.loopInfo
    
    // ループの終端に達したことを示すフラグを設定
    // 実際の処理はExecutionEngine側で行う
    return true
  }
  
  /**
   * 現在の実行位置情報を取得
   */
  getExecutionPath(): string {
    return this.contextStack
      .map((ctx, index) => {
        if (ctx.loopInfo) {
          return `${ctx.loopInfo.type}[${ctx.loopInfo.iterationCount}]`
        }
        return index === 0 ? 'main' : 'block'
      })
      .join(' > ')
  }
  
  /**
   * デバッグ用：現在の状態を出力
   */
  debug(): void {
    console.log('Execution Context Stack:')
    this.contextStack.forEach((ctx, index) => {
      console.log(`  [${index}] index=${ctx.statementIndex}/${ctx.statements.length}`)
      if (ctx.loopInfo) {
        console.log(`      loop: ${ctx.loopInfo.type}, iteration=${ctx.loopInfo.iterationCount}`)
      }
    })
  }
}